/**
 * consoleState — sessionStorage-backed navigation memory for the super-admin
 * console (P0 of the console redesign).
 *
 * Everything the operator "was doing" survives navigation within the session:
 *   - which tenant they last worked on, and which workspace sub-tab
 *   - the tenant-tree collapse state and the active directory lens
 *   - recently-visited tenants (drives the ⌘K switcher + sidebar recents)
 *   - starred tenants (persisted in localStorage — stars are a preference,
 *     not a session artifact)
 *   - whether the Design System landing was already seen this session
 *
 * Deliberately NOT a React context: plain read/write helpers + a tiny
 * subscription so multiple components stay in sync without threading a
 * provider through the router. State is namespaced per browser session —
 * a fresh tab starts clean (except stars).
 */

const SS_PREFIX = 'sa-console:';
const LS_STARS_KEY = 'sa-console:starred-tenants';
const MAX_RECENTS = 5;

type Listener = () => void;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l());
}

/** Subscribe to any console-state change. Returns an unsubscribe fn. */
export function subscribeConsoleState(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function ssGet<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function ssSet(key: string, value: unknown) {
  try {
    sessionStorage.setItem(SS_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage full / privacy mode — memory features degrade gracefully.
  }
  emit();
}

/* ─── Recent tenants ──────────────────────────────────────────────────── */

export interface RecentTenant {
  tenantId: string;
  tenantName: string;
  tenantCode: string;
}

export function getRecentTenants(): RecentTenant[] {
  return ssGet<RecentTenant[]>('recent-tenants', []);
}

/** Record a tenant visit — most recent first, deduped, capped. */
export function pushRecentTenant(entry: RecentTenant) {
  const rest = getRecentTenants().filter((r) => r.tenantId !== entry.tenantId);
  ssSet('recent-tenants', [entry, ...rest].slice(0, MAX_RECENTS));
}

/* ─── Starred tenants (localStorage — survive across sessions) ────────── */

export function getStarredTenants(): string[] {
  try {
    const raw = localStorage.getItem(LS_STARS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function toggleStarTenant(tenantId: string): boolean {
  const stars = getStarredTenants();
  const has = stars.includes(tenantId);
  const next = has ? stars.filter((s) => s !== tenantId) : [...stars, tenantId];
  try {
    localStorage.setItem(LS_STARS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  emit();
  return !has;
}

/* ─── Workspace sub-tab memory (per tenant + a global "last") ─────────── */

export type WorkspaceSection =
  | 'overview'
  | 'modules'
  | 'models'
  | 'access'
  | 'branding'
  | 'integrations'
  | 'activity';

export const WORKSPACE_SECTIONS: WorkspaceSection[] = [
  'overview', 'modules', 'models', 'access', 'branding', 'integrations', 'activity',
];

export function isWorkspaceSection(v: string | undefined): v is WorkspaceSection {
  return !!v && (WORKSPACE_SECTIONS as string[]).includes(v);
}

/** The section the operator was last on — used when switching tenants so the
 *  switch preserves place ("team-alpha → Models" ⇒ "brij-demo → Models"). */
export function getLastWorkspaceSection(): WorkspaceSection {
  return ssGet<WorkspaceSection>('last-workspace-section', 'overview');
}

export function setLastWorkspaceSection(section: WorkspaceSection) {
  ssSet('last-workspace-section', section);
}

/**
 * The logo a tenant has actually chosen, if any.
 *
 * `brand_overrides.logo` is the tenant's OWN upload. We deliberately do not
 * fall back to the platform logo: every tenant would then wear the same
 * avatar, which tells the operator nothing. No override → initials.
 */
export function tenantOwnLogo(tenant: { tenant_settings?: Record<string, any> | null }): string | null {
  const overrides = tenant.tenant_settings?.brand_overrides;
  const logo = overrides && typeof overrides === 'object' ? overrides.logo : null;
  return typeof logo === 'string' && logo.trim() ? logo : null;
}

/* ─── Tenant directory ────────────────────────────────────────────────── */

export function getTreeCollapsedNodes(): string[] {
  return ssGet<string[]>('tree-collapsed', []);
}

export function setTreeCollapsedNodes(ids: string[]) {
  ssSet('tree-collapsed', ids);
}

/** Expanded branches of the design-system tenant tree. `null` = never
 *  initialised, which lets the tree seed its own sensible default instead of
 *  opening fully every time. */
export function getDsTreeExpanded(): string[] | null {
  return ssGet<string[] | null>('ds-tree-expanded', null);
}

export function setDsTreeExpanded(ids: string[]) {
  ssSet('ds-tree-expanded', ids);
}

export function getTreeSelectedTenantId(): string | null {
  return ssGet<string | null>('tree-selected', null);
}

export function setTreeSelectedTenantId(id: string | null) {
  ssSet('tree-selected', id);
}

/* ─── Design System page memory ───────────────────────────────────────── */

/** Design System landing ("whose brand?") is shown on FIRST visit per
 *  session only; afterwards the page resumes the last edited tenant. */
export function getDesignSystemLandingSeen(): boolean {
  return ssGet<boolean>('ds-landing-seen', false);
}

export function setDesignSystemLandingSeen() {
  ssSet('ds-landing-seen', true);
}

export function getDesignSystemLastTenant(): string | null {
  return ssGet<string | null>('ds-last-tenant', null);
}

export function setDesignSystemLastTenant(id: string | null) {
  ssSet('ds-last-tenant', id);
}

/* ─── White-Label editor section memory ───────────────────────────────── */

export function getBrandSection(): string {
  return ssGet<string>('brand-section', 'basics');
}

export function setBrandSection(section: string) {
  ssSet('brand-section', section);
}
